<div class="skills-panel" [ngSwitch]="view">

    <!-- ============================================================ -->
    <!-- LIST                                                          -->
    <!-- ============================================================ -->
    <div class="skills-list" *ngSwitchCase="'list'">
        <div class="skills-list-header">
            <div class="search-field">
                <i class="ph-bold ph-magnifying-glass icon-muted icon-16" aria-hidden="true"></i>
                <input
                    type="text"
                    placeholder="Search skills"
                    [(ngModel)]="searchTerm"
                    aria-label="Search skills">
            </div>
            <button type="button" class="btn-sm btn-primary" [matMenuTriggerFor]="addMenu">
                <i class="ph-bold ph-plus icon-16" aria-hidden="true"></i>
                <span>Add</span>
            </button>
            <mat-menu #addMenu="matMenu" xPosition="before" class="gable-menu-panel">
                <button mat-menu-item (click)="onAddUpload()">
                    <i class="ph-bold ph-upload-simple icon-muted icon-16" aria-hidden="true"></i>
                    <span>Upload a file</span>
                </button>
                <button mat-menu-item (click)="onAddWriteMyself()">
                    <i class="ph-bold ph-pencil-simple icon-muted icon-16" aria-hidden="true"></i>
                    <span>Write it myself</span>
                </button>
                <button mat-menu-item (click)="onAddWriteWithGable()">
                    <i class="ph-bold ph-sparkle icon-muted icon-16" aria-hidden="true"></i>
                    <span>Write it with Gable</span>
                </button>
                <button mat-menu-item (click)="onAddFromLibrary()">
                    <i class="ph-bold ph-books icon-muted icon-16" aria-hidden="true"></i>
                    <span>Browse the PayHOA library</span>
                </button>
            </mat-menu>
        </div>

        <div class="skills-loading" *ngIf="loading" aria-busy="true">
            <i class="ph-bold ph-circle-notch icon-muted icon-16 gable-spin" aria-hidden="true"></i>
            <span>Loading skills…</span>
        </div>

        <ng-container *ngIf="!loading">
            <div class="skills-table" *ngIf="filteredSkills.length > 0; else emptyState">
                <div class="skills-table-head">
                    <span class="col-name">Skill</span>
                    <span class="col-updated">Updated</span>
                    <span class="col-author">Author</span>
                </div>
                <button
                    type="button"
                    class="skills-row"
                    *ngFor="let s of filteredSkills; trackBy: trackBySkillId"
                    [class.is-off]="!s.enabled"
                    (click)="openDetail(s)">
                    <span class="col-name">
                        <span class="skill-name">{{ s.name }}</span>
                        <span class="off-tag" *ngIf="!s.enabled">Off</span>
                    </span>
                    <span class="col-updated">{{ shortDate(s.updatedAt) }}</span>
                    <span class="col-author">{{ s.authorName }}</span>
                </button>
            </div>
            <ng-template #emptyState>
                <div class="queue-empty">
                    <i class="ph-bold ph-books" aria-hidden="true"></i>
                    <span class="empty-title">No skills yet</span>
                    <span class="empty-sub">Browse the PayHOA library or upload a file to get started.</span>
                </div>
            </ng-template>
        </ng-container>

        <p class="skills-helper">
            Gable reads a skill's description and pulls it in when the work matches. Skills change how it
            writes and decides — they never let it act without your approval.
        </p>
    </div>

    <!-- ============================================================ -->
    <!-- DETAIL                                                        -->
    <!-- ============================================================ -->
    <div class="skill-detail" *ngSwitchCase="'detail'">
        <button type="button" class="back-link" (click)="backToList()">
            <i class="ph-bold ph-arrow-left icon-16" aria-hidden="true"></i>
            <span>All skills</span>
        </button>

        <div class="skills-loading" *ngIf="detailLoading" aria-busy="true">
            <i class="ph-bold ph-circle-notch icon-muted icon-16 gable-spin" aria-hidden="true"></i>
            <span>Loading…</span>
        </div>

        <ng-container *ngIf="!detailLoading && selected as skill">
            <div class="detail-header">
                <div class="detail-title-col">
                    <h3 class="detail-name">{{ skill.name }}</h3>
                    <p class="detail-meta">
                        by {{ skill.authorName }} · updated {{ shortDate(skill.updatedAt) }}
                        <span *ngIf="skill.isEdited"> · Edited</span>
                    </p>
                </div>
                <div class="detail-actions">
                    <label class="toggle-switch">
                        <input type="checkbox" [checked]="skill.enabled" (change)="toggleEnabled()" aria-label="Enable skill">
                        <span class="toggle-track"><span class="toggle-thumb"></span></span>
                    </label>
                    <button type="button" class="icon-btn" (click)="openEdit()" aria-label="Edit skill">
                        <i class="ph-bold ph-pencil-simple icon-16" aria-hidden="true"></i>
                    </button>
                    <button type="button" class="icon-btn" [matMenuTriggerFor]="kebabMenu" aria-label="Skill options">
                        <i class="ph-bold ph-dots-three icon-16" aria-hidden="true"></i>
                    </button>
                    <mat-menu #kebabMenu="matMenu" xPosition="before" class="gable-menu-panel">
                        <button mat-menu-item (click)="tryInChat()">
                            <i class="ph-bold ph-chat-circle icon-muted icon-16" aria-hidden="true"></i>
                            <span>Try it in chat</span>
                        </button>
                        <button mat-menu-item (click)="editWithGable()">
                            <i class="ph-bold ph-sparkle icon-muted icon-16" aria-hidden="true"></i>
                            <span>Edit with Gable</span>
                        </button>
                        <button mat-menu-item (click)="triggerReplaceFile()">
                            <i class="ph-bold ph-upload-simple icon-muted icon-16" aria-hidden="true"></i>
                            <span>Replace file</span>
                        </button>
                        <button mat-menu-item (click)="downloadSkill()">
                            <i class="ph-bold ph-download-simple icon-muted icon-16" aria-hidden="true"></i>
                            <span>Download</span>
                        </button>
                        <button mat-menu-item class="menu-item-danger" (click)="confirmRemove()">
                            <i class="ph-bold ph-trash icon-16" aria-hidden="true"></i>
                            <span>Remove</span>
                        </button>
                    </mat-menu>
                </div>
            </div>

            <div class="instructions-card" *ngIf="skill.body">
                <div class="markdown-body" [innerHTML]="skill.body | markdown: 'final'"></div>
            </div>

            <div class="no-description-callout" *ngIf="!skill.description">
                <i class="ph-bold ph-info icon-16" aria-hidden="true"></i>
                <span>This skill has no description, so Gable can't match on it.</span>
            </div>

            <div class="applies-to-row">
                <div>
                    <span class="applies-label">Applies to</span>
                    <span class="applies-value">{{ skill.reachSummary }}</span>
                </div>
                <button type="button" class="link-btn" (click)="openReachEditor('detail')">Change</button>
            </div>
        </ng-container>
    </div>

    <!-- ============================================================ -->
    <!-- EDIT                                                          -->
    <!-- ============================================================ -->
    <div class="skill-edit" *ngSwitchCase="'edit'">
        <button type="button" class="back-link" (click)="cancelEdit()">
            <i class="ph-bold ph-arrow-left icon-16" aria-hidden="true"></i>
            <span>Cancel</span>
        </button>

        <div class="edit-banner" *ngIf="edit.banner">
            <i class="ph-bold ph-info icon-16" aria-hidden="true"></i>
            <span>{{ edit.banner }}</span>
        </div>

        <label class="field-label" for="skillNameInput">Skill name</label>
        <input
            id="skillNameInput"
            type="text"
            class="text-field"
            [(ngModel)]="edit.name"
            maxlength="100"
            placeholder="delinquency-escalation-ladder">
        <p class="file-name-preview">{{ fileNamePreview }}</p>

        <label class="field-label" for="skillDescInput">Description</label>
        <textarea
            id="skillDescInput"
            class="text-field"
            rows="2"
            maxlength="1000"
            [(ngModel)]="edit.description"
            placeholder="One sentence Gable can match this skill on."></textarea>

        <label class="field-label" for="skillBodyInput">Instructions</label>
        <textarea
            id="skillBodyInput"
            class="text-field body-field"
            rows="14"
            maxlength="65000"
            spellcheck="false"
            [(ngModel)]="edit.body"></textarea>

        <div class="applies-to-row">
            <div>
                <span class="applies-label">Applies to</span>
                <span class="applies-value">{{ editReachSummaryText }}</span>
            </div>
            <button type="button" class="link-btn" (click)="openReachEditor('edit')">Change</button>
        </div>

        <div class="edit-actions">
            <button type="button" class="btn-sm btn-secondary" (click)="cancelEdit()">Cancel</button>
            <button type="button" class="btn-sm btn-primary" [disabled]="!canSaveEdit || saving" (click)="saveEdit()">
                <i class="ph-bold ph-circle-notch icon-16 gable-spin" *ngIf="saving" aria-hidden="true"></i>
                <span>Save changes</span>
            </button>
        </div>
    </div>

    <!-- ============================================================ -->
    <!-- ADD — upload a file                                           -->
    <!-- ============================================================ -->
    <div class="skill-add-upload" *ngSwitchCase="'add-upload'">
        <button type="button" class="back-link" (click)="view = 'list'">
            <i class="ph-bold ph-arrow-left icon-16" aria-hidden="true"></i>
            <span>Add a skill</span>
        </button>
        <h3>Upload a file</h3>
        <p class="helper-text">Choose a .md file — Gable reads its front matter for the name and description.</p>
        <button type="button" class="btn-sm btn-primary" (click)="triggerUpload()">
            <i class="ph-bold ph-upload-simple icon-16" aria-hidden="true"></i>
            <span>Choose file…</span>
        </button>
    </div>

    <!-- ============================================================ -->
    <!-- ADD — write it with Gable                                     -->
    <!-- ============================================================ -->
    <div class="skill-add-gable" *ngSwitchCase="'add-gable'">
        <button type="button" class="back-link" (click)="view = 'list'">
            <i class="ph-bold ph-arrow-left icon-16" aria-hidden="true"></i>
            <span>Add a skill</span>
        </button>
        <h3>Write it with Gable</h3>
        <p class="helper-text">
            Describe what you want in plain words — Gable will draft the name, description, and
            instructions for you to review.
        </p>
        <textarea
            class="text-field"
            rows="6"
            maxlength="2000"
            [(ngModel)]="briefText"
            placeholder="e.g. When coding transactions, always check vendor history first…"></textarea>
        <div class="edit-actions">
            <button type="button" class="btn-sm btn-secondary" (click)="view = 'list'">Cancel</button>
            <button type="button" class="btn-sm btn-primary" [disabled]="!briefText.trim() || drafting" (click)="onDraftWithGable()">
                <i class="ph-bold ph-circle-notch icon-16 gable-spin" *ngIf="drafting" aria-hidden="true"></i>
                <span>Draft it</span>
            </button>
        </div>
    </div>

    <!-- ============================================================ -->
    <!-- ADD — browse the PayHOA library                               -->
    <!-- ============================================================ -->
    <div class="skill-add-library" *ngSwitchCase="'add-library'">
        <button type="button" class="back-link" (click)="view = 'list'">
            <i class="ph-bold ph-arrow-left icon-16" aria-hidden="true"></i>
            <span>Add a skill</span>
        </button>
        <h3>The PayHOA library</h3>
        <div class="skills-loading" *ngIf="startersLoading" aria-busy="true">
            <i class="ph-bold ph-circle-notch icon-muted icon-16 gable-spin" aria-hidden="true"></i>
            <span>Loading…</span>
        </div>
        <div class="starter-row" *ngFor="let s of starters; trackBy: trackByStarterKey">
            <div class="starter-text">
                <span class="starter-name">{{ s.name }}</span>
                <span class="starter-desc">{{ s.description }}</span>
            </div>
            <button type="button" class="btn-sm btn-secondary" (click)="addStarter(s)">Add</button>
        </div>
    </div>

    <!-- ============================================================ -->
    <!-- REACH EDITOR                                                  -->
    <!-- ============================================================ -->
    <div class="reach-editor" *ngSwitchCase="'reach'">
        <button type="button" class="back-link" (click)="cancelReach()">
            <i class="ph-bold ph-arrow-left icon-16" aria-hidden="true"></i>
            <span>Back</span>
        </button>
        <h3>Who does this apply to?</h3>

        <div class="mode-toggle">
            <button type="button" class="mode-btn" [class.is-active]="reach.mode === 'all'" (click)="setReachMode('all')">
                All organizations
            </button>
            <button type="button" class="mode-btn" [class.is-active]="reach.mode === 'selected'" (click)="setReachMode('selected')">
                Selected organizations
            </button>
        </div>

        <ng-container *ngIf="reach.mode === 'selected'">
            <div class="reach-section">
                <p class="reach-question">Where are they?</p>
                <p class="reach-helper">All 50 states. Narrow it down only if the skill is state-specific.</p>

                <div class="state-pills" *ngIf="reach.filters.states.length > 0">
                    <span class="state-pill" *ngFor="let st of reach.filters.states; trackBy: trackByState">
                        {{ stateLabel(st) }}
                        <button type="button" (click)="removeState(st)" aria-label="Remove state">
                            <i class="ph-bold ph-x icon-12" aria-hidden="true"></i>
                        </button>
                    </span>
                </div>

                <button type="button" class="fchip" *ngIf="!statesExpanded" (click)="statesExpanded = true">
                    <i class="ph-bold ph-plus icon-12" aria-hidden="true"></i>
                    <span>Choose states</span>
                </button>

                <div class="state-picker" *ngIf="statesExpanded">
                    <input
                        type="text"
                        class="text-field"
                        placeholder="Search states"
                        [(ngModel)]="stateSearch"
                        aria-label="Search states">
                    <div class="state-checklist">
                        <label class="state-check-row" *ngFor="let st of filteredStates; trackBy: trackByState">
                            <input type="checkbox" [checked]="isStateSelected(st.value)" (change)="toggleState(st.value)">
                            <span>{{ st.label }}</span>
                        </label>
                    </div>
                    <button type="button" class="link-btn" (click)="statesExpanded = false">Done</button>
                </div>
            </div>

            <div class="reach-section">
                <p class="reach-question">Which communities?</p>
                <div class="size-chips">
                    <button type="button" class="fchip" [class.on]="isEveryOrgBand()" (click)="setSizeAll()">
                        <span>Every organization</span>
                        <span class="chip-count" *ngIf="bandCounts['all'] !== undefined">{{ bandCounts['all'] }}</span>
                    </button>
                    <button type="button" class="fchip" [class.on]="hasSizeBand('under_100')" (click)="toggleSizeBand('under_100')">
                        <span>Under 100 units</span>
                        <span class="chip-count" *ngIf="bandCounts['under_100'] !== undefined">{{ bandCounts['under_100'] }}</span>
                    </button>
                    <button type="button" class="fchip" [class.on]="hasSizeBand('100_500')" (click)="toggleSizeBand('100_500')">
                        <span>100–500 units</span>
                        <span class="chip-count" *ngIf="bandCounts['100_500'] !== undefined">{{ bandCounts['100_500'] }}</span>
                    </button>
                    <button type="button" class="fchip" [class.on]="hasSizeBand('over_500')" (click)="toggleSizeBand('over_500')">
                        <span>Over 500 units</span>
                        <span class="chip-count" *ngIf="bandCounts['over_500'] !== undefined">{{ bandCounts['over_500'] }}</span>
                    </button>
                    <button type="button" class="fchip" [class.on]="reach.filters.customRange !== null" (click)="setSizeCustom()">
                        <span>Custom range</span>
                    </button>
                </div>
                <div class="custom-range-inputs" *ngIf="reach.filters.customRange as range">
                    <input type="number" class="text-field" min="0" [(ngModel)]="range.min" (ngModelChange)="onReachChanged()" aria-label="Minimum units">
                    <span>to</span>
                    <input type="number" class="text-field" min="0" [(ngModel)]="range.max" (ngModelChange)="onReachChanged()" aria-label="Maximum units">
                    <span>units</span>
                </div>
            </div>

            <div class="reach-summary-bar" [class.zero]="!reachPreviewLoading && reachPreview?.count === 0">
                <span *ngIf="reachPreviewLoading">Calculating…</span>
                <ng-container *ngIf="!reachPreviewLoading && reachPreview as preview">
                    <strong>{{ preview.count }} of {{ preview.total }} organizations</strong>
                    <span *ngIf="preview.examples.length > 0"> — {{ preview.examples.join(', ') }}</span>
                </ng-container>
            </div>
            <div class="reach-zero-warning" *ngIf="!reachPreviewLoading && reachPreview?.count === 0">
                <i class="ph-bold ph-warning icon-16" aria-hidden="true"></i>
                <span>No organizations currently match these filters.</span>
                <label class="confirm-zero">
                    <input type="checkbox" [(ngModel)]="reachConfirmZero">
                    <span>Save anyway</span>
                </label>
            </div>

            <div class="reach-section">
                <p class="reach-question">Exceptions</p>
                <p class="reach-helper">
                    Exceptions stay put as organizations come and go. Everything else follows the filters,
                    so new communities that match are covered automatically.
                </p>
                <div class="exception-search">
                    <i class="ph-bold ph-magnifying-glass icon-muted icon-16" aria-hidden="true"></i>
                    <input
                        type="text"
                        placeholder="Search organizations"
                        [(ngModel)]="exceptionSearch"
                        (ngModelChange)="onExceptionSearchChange($event)"
                        aria-label="Search organizations for an exception">
                </div>
                <div class="exception-results" *ngIf="exceptionResults.length > 0">
                    <div class="exception-result-row" *ngFor="let org of exceptionResults; trackBy: trackByOrgLookup">
                        <span>{{ org.name }}</span>
                        <button type="button" class="btn-sm btn-secondary" *ngIf="org.matchesFilters" (click)="addException(org, 'never')">Exclude</button>
                        <button type="button" class="btn-sm btn-secondary" *ngIf="!org.matchesFilters" (click)="addException(org, 'always')">Include</button>
                    </div>
                </div>
                <div class="exception-tags" *ngIf="reach.exceptions.length > 0">
                    <span
                        class="exception-tag"
                        [class.always]="ex.type === 'always'"
                        [class.never]="ex.type === 'never'"
                        *ngFor="let ex of reach.exceptions; trackBy: trackByException">
                        <span class="exception-type">{{ ex.type === 'always' ? 'Always' : 'Never' }}</span>
                        <span>{{ ex.name }}</span>
                        <button type="button" (click)="removeException(ex)" aria-label="Remove exception">
                            <i class="ph-bold ph-x icon-12" aria-hidden="true"></i>
                        </button>
                    </span>
                </div>
            </div>
        </ng-container>

        <div class="edit-actions">
            <button type="button" class="btn-sm btn-secondary" (click)="cancelReach()">Cancel</button>
            <button type="button" class="btn-sm btn-primary" [disabled]="!canSaveReach" (click)="saveReach()">Save</button>
        </div>
    </div>

    <input #uploadInput type="file" accept=".md" class="visually-hidden" (change)="onUploadFileSelected($event)">
    <input #replaceInput type="file" accept=".md" class="visually-hidden" (change)="onReplaceFileSelected($event)">
</div>
